import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AppBar, IconButton, Link, Stack, Toolbar, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AppSettingsMenu from 'components/layout/AppSettingsMenu';
import AppAuthIndicator from 'components/layout/AppAuthIndicator';
import AppLauncher from 'components/appLauncher/AppLauncher';
import NotificationBell from 'components/notifications/NotificationBell';
import DpTopBarAdminLinks from 'features/decisionPoint/components/DpTopBarAdminLinks';
import HelpChatModal from 'components/help/HelpChatModal';
import { APP_TOP_BAR_ACTION_HIT_PX, APP_TOP_BAR_ACTION_ICON_PX, APP_TOP_BAR_Z_INDEX } from 'layout/appTopBarLayout';
import { useAuth } from 'hooks/useAuth';
import { useHelpWorkflow } from 'context/HelpWorkflowContext';
import PageGuideHeaderToggle from 'components/pageGuide/PageGuideHeaderToggle';
import {
  healthContoursMapPath,
  isHealthContoursPath,
} from 'pages/healthcontours/healthContoursHost';
import { healthContoursGoldMetalTextSx } from 'pages/healthcontours/healthContoursMarketingTokens';
import { isAsiMarketingHost, marketingOverviewPath } from 'features/asi/marketing/asiMarketingRouting';
import { getPlatformBranding } from 'config/platformBranding';
import { asiGoldMetalTextSx } from 'features/asi/marketing/asiMarketingTokens';
import { VoxPontImmersiveEnterButton } from 'features/voxpontShared/VoxPontImmersiveChromeControls';

/**
 * Global top bar on every route: optional host wordmark, DP admin links, settings,
 * notifications, help, FusionMSI app launcher, and account chip. Fixed above page overlays.
 * ClinicalHUD uses this same FusionMSI chrome (no host wordmark).
 */
function GlobalTopBarChrome() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { modalOpen, openHelpModal, closeHelpModal } = useHelpWorkflow();
  const showAsiWordmark = isAsiMarketingHost();
  const showHealthContoursWordmark =
    !showAsiWordmark && isHealthContoursPath(pathname);
  const asiBranding = showAsiWordmark ? getPlatformBranding() : null;

  if (!user) return null;

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: APP_TOP_BAR_Z_INDEX,
          pointerEvents: 'auto',
        }}
      >
        <Toolbar
          variant="dense"
          disableGutters
          sx={{
            minHeight: { xs: 56, sm: 56 },
            justifyContent: 'flex-end',
            px: { xs: 1, sm: 2 },
            gap: 0.5,
          }}
        >
          {showAsiWordmark ? (
            <Link
              component={RouterLink}
              to={marketingOverviewPath()}
              underline="none"
              aria-label={`${asiBranding.name} home`}
              data-testid="asi-top-bar-wordmark"
              sx={{ mr: 'auto', display: 'flex', alignItems: 'center', pl: { xs: 1, sm: 0.5 } }}
            >
              <Typography
                component="span"
                variant="h6"
                sx={(theme) => ({
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  ...(theme.palette.mode === 'dark'
                    ? asiGoldMetalTextSx
                    : { color: 'primary.main' }),
                })}
              >
                {asiBranding.assistantName}
              </Typography>
            </Link>
          ) : null}
          {showHealthContoursWordmark ? (
            <Link
              component={RouterLink}
              to={healthContoursMapPath()}
              underline="none"
              aria-label="HealthContours home"
              data-testid="healthcontours-top-bar-wordmark"
              sx={{ mr: 'auto', display: 'flex', alignItems: 'center', pl: { xs: 1, sm: 0.5 } }}
            >
              <Typography
                component="span"
                variant="h6"
                sx={(theme) => ({
                  fontWeight: 700,
                  letterSpacing: '0.01em',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  ...(theme.palette.mode === 'dark'
                    ? healthContoursGoldMetalTextSx
                    : { color: 'primary.main' }),
                })}
              >
                HealthContours
              </Typography>
            </Link>
          ) : null}
          <VoxPontImmersiveEnterButton />
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap">
            <DpTopBarAdminLinks />
            <AppSettingsMenu />
            <PageGuideHeaderToggle />
            {user ? <NotificationBell /> : null}
            <IconButton
              type="button"
              aria-label="Help"
              onClick={openHelpModal}
              sx={{
                width: APP_TOP_BAR_ACTION_HIT_PX,
                height: APP_TOP_BAR_ACTION_HIT_PX,
                flexShrink: 0,
                color: 'primary.main',
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: APP_TOP_BAR_ACTION_ICON_PX }} />
            </IconButton>
            <AppLauncher />
            <AppAuthIndicator />
          </Stack>
        </Toolbar>
      </AppBar>
      <HelpChatModal open={modalOpen} onClose={closeHelpModal} />
    </>
  );
}

export default GlobalTopBarChrome;
